import React, { useMemo, useState } from 'react';
import { useForm, UseFormReturn, FieldError } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FlexyFormLabel } from '@europrocurement/flexy-components';
import {
    FormObject,
    ExtandedFormContext,
    onChangeInputType,
} from '@europrocurement/flexy-form/components/FlexyForm/FlexyFormTypes';
import { GoogleMapAutocomplete, GoogleMapAutocompleteProps } from '@europrocurement/l2d-modules';
import {
    usePrescripteurForm,
    PaysOption,
    Step2Data,
} from '../../providers/PrescripteurFormContext';

type AddressFormatted = Parameters<GoogleMapAutocompleteProps['onChange']>[0];

type StepAdresseClassificationProps = {
    onNext: () => void;
    onBack: () => void;
};

const buildFullAddress = (fc: ExtandedFormContext, override: Record<string, string> = {}) => {
    const get = (key: string) => override[key] ?? (fc.getValue(key) as string) ?? '';
    return ['rue', 'adresseComplement', 'codePostal', 'ville'].map(get).filter(Boolean).join(', ');
};

const syncFullAddress: onChangeInputType = (event, input, formContext) => {
    formContext.setValue(
        'fullAddress',
        buildFullAddress(formContext, {
            [input.name]: (event.target as HTMLInputElement).value,
        }),
        { shouldValidate: true },
    );
};

const StepAdresseClassification: React.FunctionComponent<StepAdresseClassificationProps> =
    function ({ onNext, onBack }) {
        const { step1, step2, setStep2, walletOptions, cspOptions, paysOptions, optionsLoading } =
            usePrescripteurForm();

        const [manualMode, setManualMode] = useState(false);

        const defaultPays = useMemo(() => {
            if (step2?.pays) {
                return paysOptions.find((p) => p.codeIso2 === step2.pays) ?? null;
            }
            if (step1?.isEtrangere === false) {
                return paysOptions.find((p) => p.codeIso2 === 'FR') ?? null;
            }
            return null;
        }, [paysOptions, step1?.isEtrangere, step2?.pays]);

        const methods = useForm({
            mode: 'onTouched',
            defaultValues: {
                fullAddress: step2?.fullAddress ?? '',
                rue: step2?.rue ?? '',
                adresseComplement: step2?.adresseComplement ?? '',
                codePostal: step2?.codePostal ?? '',
                ville: step2?.ville ?? '',
                pays: defaultPays,
                portefeuille: step2?.portefeuille ?? null,
                csp: step2?.csp ?? null,
            },
        });

        const saveToContext = () => {
            const allValues = methods.getValues();
            const paysOption = allValues.pays as PaysOption | null;
            setStep2({
                fullAddress: (allValues.fullAddress as string) ?? '',
                rue: (allValues.rue as string) ?? '',
                adresseComplement: (allValues.adresseComplement as string) ?? '',
                codePostal: (allValues.codePostal as string) ?? '',
                ville: (allValues.ville as string) ?? '',
                pays: paysOption?.codeIso2 ?? '',
                portefeuille: (allValues.portefeuille as Step2Data['portefeuille']) ?? null,
                csp: (allValues.csp as Step2Data['csp']) ?? null,
            });
        };

        const handleNext = () => {
            saveToContext();
            onNext();
        };

        const handleBack = () => {
            saveToContext();
            onBack();
        };

        const formStructure = useMemo(
            (): FormStructure[] => [
                {
                    type: 'customItem',
                    name: 'fullAddress',
                    lg: 12,
                    md: 12,
                    sm: 12,
                    renderField: (fc) => {
                        const { ref, name } = fc.register('fullAddress', {
                            required: "L'adresse est obligatoire",
                        });
                        const addressError = fc.formState.errors?.fullAddress as
                            | FieldError
                            | undefined;
                        const currentValue = (fc.getValue('fullAddress') as string) ?? '';

                        const handleAddressChange = (formattedAddress: AddressFormatted) => {
                            fc.setValue('rue', formattedAddress.address);
                            fc.setValue(
                                'adresseComplement',
                                formattedAddress.addressComplement ?? '',
                            );
                            fc.setValue('codePostal', formattedAddress.postalCode);
                            fc.setValue('ville', formattedAddress.city);
                            fc.setValue(
                                'pays',
                                paysOptions.find(
                                    (p) => p.codeIso2 === formattedAddress.countryCode,
                                ) ?? null,
                            );
                            fc.setValue(
                                'fullAddress',
                                [
                                    formattedAddress.address,
                                    formattedAddress.addressComplement,
                                    formattedAddress.postalCode,
                                    formattedAddress.city,
                                ]
                                    .filter(Boolean)
                                    .join(', '),
                                { shouldValidate: true },
                            );
                        };

                        if (manualMode) {
                            return (
                                <Box>
                                    <input
                                        type="hidden"
                                        ref={ref}
                                        name={name}
                                    />
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => setManualMode(false)}
                                        sx={{ p: 0, textDecoration: 'underline' }}
                                    >
                                        Rechercher une adresse
                                    </Button>
                                </Box>
                            );
                        }

                        return (
                            <Box>
                                <input
                                    type="hidden"
                                    ref={ref}
                                    name={name}
                                />
                                <FlexyFormLabel required>Adresse</FlexyFormLabel>
                                <Box
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'background.paper',
                                        },
                                    }}
                                >
                                    <GoogleMapAutocomplete
                                        inputValue={currentValue}
                                        placeholder="Rechercher une adresse..."
                                        onChange={handleAddressChange}
                                        onKeyUp={() => {}}
                                        isFrenchAddress
                                        hasError={!!addressError}
                                    />
                                </Box>
                                {addressError && (
                                    <Typography
                                        color="error"
                                        variant="caption"
                                        sx={{ mt: 0.5, display: 'block' }}
                                    >
                                        {addressError.message}
                                    </Typography>
                                )}
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setManualMode(true)}
                                    sx={{ mt: 1, p: 0, textDecoration: 'underline' }}
                                >
                                    Je ne trouve pas mon adresse
                                </Button>
                            </Box>
                        );
                    },
                },
                {
                    type: 'text',
                    name: 'rue',
                    inputlabel: 'Adresse',
                    display: manualMode,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    rules: { required: "L'adresse est obligatoire" },
                    onChangeInput: syncFullAddress,
                },
                {
                    type: 'text',
                    name: 'adresseComplement',
                    inputlabel: "Complément d'adresse",
                    display: manualMode,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    onChangeInput: syncFullAddress,
                },
                {
                    type: 'text',
                    name: 'codePostal',
                    inputlabel: 'Code postal',
                    display: manualMode,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    onChangeInput: syncFullAddress,
                },
                {
                    type: 'text',
                    name: 'ville',
                    inputlabel: 'Ville',
                    display: manualMode,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    onChangeInput: syncFullAddress,
                },
                {
                    type: 'select',
                    name: 'pays',
                    inputlabel: 'Pays',
                    display: manualMode,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    options: paysOptions.map((p) => ({
                        ...p,
                        value: p.codeIso2 ?? '',
                        label: p.libelle ?? '',
                    })),
                    getOptionLabel: ({ libelle }: { libelle?: string }) => libelle ?? '',
                    getOptionValue: ({ codeIso2 }: { codeIso2?: string }) => codeIso2 ?? '',
                    rules: manualMode ? { required: 'Le pays est obligatoire' } : {},
                },
                {
                    type: 'select',
                    name: 'portefeuille',
                    inputlabel: 'Portefeuille',
                    options: walletOptions.map((o) => ({
                        ...o,
                        value: String(o.id),
                        label: o.libelle ?? '',
                    })),
                    getOptionLabel: ({ libelle }: { libelle: string }) => libelle,
                    getOptionValue: ({ id }: { id: string }) => id,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    disabled: optionsLoading,
                    rules: { required: 'Le portefeuille est obligatoire' },
                },
                {
                    type: 'select',
                    name: 'csp',
                    inputlabel: 'Catégorie socioprofessionnelle',
                    options: cspOptions.map((o) => ({
                        ...o,
                        value: String(o.id),
                        label: o.libelle ?? '',
                    })),
                    getOptionLabel: ({ libelle }: { libelle: string }) => libelle,
                    getOptionValue: ({ id }: { id: string }) => id,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    disabled: optionsLoading,
                    rules: { required: 'La catégorie socioprofessionnelle est obligatoire' },
                },
            ],
            [walletOptions, cspOptions, paysOptions, optionsLoading, manualMode],
        );

        return (
            <Box>
                <FlexyForm
                    formContext={methods as unknown as UseFormReturn<FormObject>}
                    formStructure={formStructure}
                    onSubmit={handleNext}
                    submitButton={{ displayed: false }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{ padding: '16px 24px' }}
                    >
                        Précédent
                    </Button>
                    <Button
                        variant="contained"
                        onClick={methods.handleSubmit(handleNext)}
                        sx={{ padding: '16px 24px' }}
                    >
                        Suivant
                    </Button>
                </Box>
            </Box>
        );
    };

export default StepAdresseClassification;
